import React, { useState, useEffect, useRef } from "react";
import { toast } from "@shared/ui/use-toast";

// Sub-components
import { InvoiceDetails } from "./components/InvoiceDetails";
import { AdjustmentCard } from "./components/AdjustmentCard";
import { GenerationProgress } from "./components/GenerationProgress";
import { BillResultsDashboard } from "./components/BillResultsDashboard";
import { ApproveSendModal } from "./components/ApproveSendModal";
import { ClosureRequirements } from "./components/ClosureRequirements";

// Icons
import {
  Clock,
  Wallet,
  CreditCard,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { useParams } from "react-router-dom";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import {
  useBillSummary,
  useGenerateBulkBills,
  useGeneratedBills,
  useApproveBulkBill,
  useCloseBulkRequest,
  useTrackBillsProgress,
  useGetTrackBillsProgress,
  useRetryBills,
  useBillConfigMeta,
} from "@features/cx/disconnect/hooks";
import { disconnectionApi } from "@features/cx/disconnect/api";
import { queryClient } from "@shared/api/queries/queryClient";
import { normalizeDate } from "@shared/utils/dateUtils";

type ViewMode = "config" | "generating" | "results";

interface BillGenerationBulkDisconnectProps {
  currentStatus?: string;
  requestDetails?: any;
}

const BillGenerationBulkDisconnect: React.FC<
  BillGenerationBulkDisconnectProps
> = ({ currentStatus, requestDetails }) => {
  const { id } = useParams<{ id: string }>();
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const { mutateAsync: generateBills, isPending: isGenerating } =
    useGenerateBulkBills();
  const { data: billSummary } = useBillSummary(id);
  const { mutateAsync: approveBulkBill, isPending: isApproving } =
    useApproveBulkBill();
  const { mutateAsync: closeRequest, isPending: isClosing } =
    useCloseBulkRequest(id || "");
  const { mutateAsync: retryBills, isPending: isRetrying } = useRetryBills();
  const { data: billConfigMeta } = useBillConfigMeta(Number(remoteUtilityId));

  const [viewMode, setViewMode] = useState<ViewMode>("config");
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [delayedDashboardTaskId, setDelayedDashboardTaskId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const tid = requestDetails?.additionalData?.taskId;
    if (tid) {
      const timer = setTimeout(() => {
        setDelayedDashboardTaskId(tid);
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      setDelayedDashboardTaskId(null);
    }
  }, [requestDetails?.additionalData?.taskId]);

  const { data: progressData, isError, error } = useTrackBillsProgress(taskId);
  const { data: trackData } = useGetTrackBillsProgress(delayedDashboardTaskId);

  const [invoiceConfig, setInvoiceConfig] = useState({
    startDate: "",
    endDate: "",
    paymentDueDays: null,
    template: "",
    templateSource: "",
  });

  const [adjustments, setAdjustments] = useState({
    securityDeposit: false,
    credits: false,
    paymentAgreements: false,
  });

  const billRunId = requestDetails?.additionalData?.billRunId;

  const { data: generatedBillsResponse, isLoading: isLoadingBills } =
    useGeneratedBills({
      run_id: billRunId,
      remote_utility_id: Number(remoteUtilityId),
      page: 1,
      limit: 50,
    });

  const [closureRequirements, setClosureRequirements] = useState({
    billsSent: false,
    readingsCollected: false,
    notificationsDone: false,
  });

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  const isBillGenerationInProgress =
    currentStatus === "Bill Generation In Progress";

  // --- Track Progress ---
  const [trackingErrorMsg, setTrackingErrorMsg] = useState<string | null>(null);
  const [generationStats, setGenerationStats] = useState({
    processed: 0,
    successful: 0,
    failed: 0,
    total: 0,
    isCompleted: false,
  });

  // Handle Tracking Errors
  useEffect(() => {
    if (isError && taskId) {
      const errorMsg = (error as any)?.message || "Tracking failed.";
      setTrackingErrorMsg(errorMsg);
      setTaskId(null);
      toast({
        title: "Tracking Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  }, [isError, error, taskId]);

  // Reactive Sync for Progress Data (polling managed by useTrackBillsProgress hook)
  useEffect(() => {
    if (progressData) {
      const processed = progressData.processed || 0;
      const total = progressData.total || 0;
      const calculatedProgress =
        total > 0 ? Math.round((processed / total) * 100) : 0;
      const currentProgress = progressData.progress || calculatedProgress;

      setProgress(currentProgress);
      setGenerationStats({
        processed: processed,
        successful: progressData.successful || 0,
        failed: progressData.failed || 0,
        total: total,
        isCompleted: currentProgress >= 100 || progressData.completed,
      });

      if (currentProgress >= 100 || progressData.completed) {
        if (viewMode === "generating") {
          setTimeout(() => {
            setViewMode("results");
            setTaskId(null);
          }, 1500);
        }
      }
    }
  }, [progressData, viewMode]);

  // Handle initial resume if status is in progress
  useEffect(() => {
    if (billRunId && viewMode === "config" && !taskId) {
      setViewMode("results");
    }
  }, [billRunId, taskId, viewMode]);

  // Autopopulate data from additionalData
  useEffect(() => {
    const additionalData = requestDetails?.additionalData;
    if (additionalData) {
      setInvoiceConfig((prev) => ({
        ...prev,
        startDate:
          normalizeDate(additionalData.invoiceStartDate) || prev.startDate,
        endDate: normalizeDate(additionalData.invoiceEndDate) || prev.endDate,
        paymentDueDays: additionalData.paymentDueIn || prev.paymentDueDays,
        template: additionalData.billConfigId?.toString() || prev.template,
        templateSource:
          additionalData.billConfigType ||
          additionalData.bill_config_type ||
          prev.templateSource,
      }));

      setAdjustments({
        securityDeposit: additionalData.applySecurityDeposit ?? false,
        credits: additionalData.applyCredits ?? false,
        paymentAgreements: additionalData.applyPaymentAgreements ?? false,
      });
    }
  }, [requestDetails]);

  // --- Handlers ---
  const handleStartGeneration = async () => {
    if (!id) return;

    // Look up the source from metadata based on selected template ID
    const selectedTemplate = billConfigMeta?.find(
      (item: any) => item.id.toString() === invoiceConfig.template.toString(),
    );
    const configType = selectedTemplate?.source || invoiceConfig.templateSource;

    // Validation: Check if all required fields are present
    if (
      !invoiceConfig.startDate ||
      !invoiceConfig.endDate ||
      !invoiceConfig.template ||
      !configType
    ) {
      toast({
        title: "Validation Error",
        description: "Bill configuration type and dates are required.",
        variant: "destructive",
      });
      if (viewMode === "results") {
        setViewMode("config");
      }
      return;
    }

    setViewMode("generating");
    setProgress(0);
    setGenerationStats({
      processed: 0,
      successful: 0,
      failed: 0,
      total: 0,
      isCompleted: false,
    });
    setTrackingErrorMsg(null);

    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      invoice_start_date: invoiceConfig.startDate,
      invoice_end_date: invoiceConfig.endDate,
      bill_config_id: Number(invoiceConfig.template),
      bill_config_type: configType,
      payment_due_in: invoiceConfig.paymentDueDays,
      apply_security_deposit: adjustments.securityDeposit,
      apply_credits: adjustments.credits,
      apply_payment_agreements: adjustments.paymentAgreements,
    };

    try {
      const response = await generateBills({ batchId: id, payload });
      const tId = response?.task_id || response?.taskId;
      if (tId) {
        setViewMode("generating");
        setTimeout(() => {
          setTaskId(tId);
        }, 10000);
      }
    } catch (error: any) {
      setTrackingErrorMsg(error?.message || "Failed to initiate generation.");
      // The error is also handled by the hook which might show a toast
    }
  };

  const handleApproveAndSend = () => {
    setIsApproveModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!billRunId) {
      toast({
        title: "Error",
        description: "No Bill Run ID found to approve.",
        variant: "destructive",
      });
      return;
    }

    try {
      await approveBulkBill(billRunId);
      setIsApproveModalOpen(false);
      toast({
        title: "Success",
        description: "Bills have been approved and sent successfully.",
      });
      queryClient.refetchQueries({
        queryKey: ["bx", "bulkBilling", "list"],
      });
      setClosureRequirements({ ...closureRequirements, billsSent: true });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to approve bills.",
        variant: "destructive",
      });
    }
  };

  const handleRegenerate = () => {
    handleStartGeneration();
  };

  const handleRegenFailed = async () => {
    const runId = requestDetails?.additionalData?.billRunId;
    if (!runId) {
      toast({
        title: "Error",
        description: "No bill run session found to retry.",
        variant: "destructive",
      });
      return;
    }

    const failedConsumers = (trackData?.errors || progressData?.errors || [])
      .map((err: any) => err.consumer_no || err.consumerNo)
      .filter(Boolean);

    try {
      setProgress(0);
      setGenerationStats({
        processed: 0,
        successful: 0,
        failed: 0,
        total: 0,
        isCompleted: false,
      });
      setTrackingErrorMsg(null);

      const response = await retryBills({
        runId,
        payload: {
          remote_utility_id: remoteUtilityId,
          consumer_nos: failedConsumers,
        },
      });

      const tId = response?.task_id || response?.taskId;
      if (tId) {
        setViewMode("generating");
        setTimeout(() => {
          setTaskId(tId);
        }, 10000);
      }
    } catch (error: any) {
      toast({
        title: "Retry Failed",
        description: error.message || "Could not initiate regeneration.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadLogs = () => {
    toast({
      title: "Log Export Started",
      description: "Generating failed logs CSV file...",
    });
  };

  const isConfigDisabled = viewMode !== "config" || isBillGenerationInProgress;

  // --- Render ---
  return (
    <div className="space-y-8 pb-12">
      {/* SECTION 1: CONFIGURATION (Always visible except when we might want a clean start, 
          but per user request: visible and disabled after generation starts) */}
      {(viewMode === "config" ||
        viewMode === "generating" ||
        viewMode === "results") && (
        <div className="space-y-6 animate-in fade-in duration-500">
          <InvoiceDetails
            config={invoiceConfig}
            onChange={setInvoiceConfig}
            disabled={isConfigDisabled}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <AdjustmentCard
              title="Apply Security Deposits"
              description="Automatically adjust bills with security deposits"
              icon={<Clock className="w-5 h-5" />}
              isActive={adjustments.securityDeposit}
              onToggle={(checked) =>
                setAdjustments({ ...adjustments, securityDeposit: checked })
              }
              variant="blue"
              disabled={isConfigDisabled}
              stats={{
                label1: "Consumers with Deposit",
                value1: billSummary?.securityDeposit?.consumersWithDeposit,
                label2: "Total Deposit Amount",
                value2:
                  currency +
                  " " +
                  billSummary?.securityDeposit?.totalDepositAmount,
              }}
            />

            <AdjustmentCard
              title="Apply Credits to Current Bills"
              description="Automatically adjust bills with outstanding credits"
              icon={<Wallet className="w-5 h-5" />}
              isActive={adjustments.credits}
              onToggle={(checked) =>
                setAdjustments({ ...adjustments, credits: checked })
              }
              variant="indigo"
              disabled={isConfigDisabled}
              stats={{
                label1: "Consumers with Credit",
                value1: billSummary?.credits?.consumersWithCredit,
                label2: "Total Credit Amount",
                value2:
                  currency + " " + billSummary?.credits?.totalCreditAmount,
              }}
            />

            <AdjustmentCard
              title="Payment Agreements Active"
              description="Include remaining installments in final bill"
              icon={<CreditCard className="w-5 h-5" />}
              isActive={adjustments.paymentAgreements}
              onToggle={(checked) =>
                setAdjustments({ ...adjustments, paymentAgreements: checked })
              }
              variant="purple"
              disabled={isConfigDisabled}
              stats={{
                label1: "Consumers with Agreements",
                value1: billSummary?.paymentAgreements?.consumersWithAgreements,
                label2: "Total Remaining Value",
                value2:
                  currency +
                  " " +
                  billSummary?.paymentAgreements?.totalRemainingValue,
              }}
            />
          </div>

          {viewMode === "config" && !isBillGenerationInProgress && (
            <div className="flex justify-center pt-4">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 h-14 px-10 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-95 text-base font-bold gap-3"
                onClick={handleStartGeneration}
                disabled={isGenerating}
              >
                <FileSpreadsheet className="w-5 h-5" />
                {isGenerating
                  ? "Initiating..."
                  : "Generate Bills for All Consumers"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* SECTION 2: PROGRESS (Shown below config during generation) */}
      {viewMode === "generating" && (
        <div className="pt-8 border-t border-dashed border-neutral-200 animate-in slide-in-from-bottom-5 duration-500">
          <GenerationProgress
            progress={progress}
            totalCount={generationStats.total}
            processed={generationStats.processed}
            successful={generationStats.successful}
            failed={generationStats.failed}
            isError={!!trackingErrorMsg}
            errorMessage={trackingErrorMsg || ""}
          />
          {trackingErrorMsg && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setViewMode("config");
                  setTrackingErrorMsg(null);
                }}
                className="rounded-lg border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
              >
                Back to Configuration
              </Button>
            </div>
          )}
        </div>
      )}

      {/* SECTION 3: RESULTS (Shown after generation) */}
      {viewMode === "results" && (
        <div className="space-y-10 animate-in fade-in slide-in-from-top-4 duration-700">
          <BillResultsDashboard
            onApprove={handleApproveAndSend}
            onRegenerateAll={handleRegenerate}
            onRegenFailed={handleRegenFailed}
            onDownloadLogs={handleDownloadLogs}
            data={generatedBillsResponse?.result}
            totalCount={generatedBillsResponse?.count}
            isLoading={isLoadingBills}
            isRegenerating={isGenerating || isRetrying}
            isApproving={isApproving}
            taskId={requestDetails?.additionalData?.taskId}
            currentStatus={requestDetails?.statusDisplay}
            generationStats={progressData}
            trackData={trackData}
          />

          <ClosureRequirements
            requirements={closureRequirements}
            onUpdate={setClosureRequirements}
            isDisabled={requestDetails?.statusDisplay === "Closed"}
          />
          {requestDetails?.statusDisplay !== "Closed" && (
            <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
              <Button
                className="bg-blue-600 hover:bg-blue-700 h-11 px-8 font-bold rounded-lg shadow-lg shadow-blue-100 transition-all active:scale-95 gap-2"
                disabled={
                  !closureRequirements.billsSent ||
                  !closureRequirements.readingsCollected ||
                  !closureRequirements.notificationsDone ||
                  isClosing
                }
                onClick={async () => {
                  try {
                    await closeRequest({
                      is_bills_sent: closureRequirements.billsSent,
                      is_final_readings_collected:
                        closureRequirements.readingsCollected,
                      is_customer_notification:
                        closureRequirements.notificationsDone,
                    });
                    toast({
                      title: "Success",
                      description:
                        "Disconnection request has been closed successfully.",
                    });
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error.message || "Failed to close request.",
                      variant: "destructive",
                    });
                  }
                }}
              >
                {isClosing && <Loader2 className="w-4 h-4 animate-spin" />}
                Finish Bill Generation
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ApproveSendModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={handleConfirmApprove}
        stats={{
          billCount: generatedBillsResponse?.count,
          emailCount: generatedBillsResponse?.count,
        }}
      />
    </div>
  );
};

export default BillGenerationBulkDisconnect;
